import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCalendarCheck, faDownload, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import { bookingsApi, customerApi, servicesLoVApi, staffApi } from '@api/api';
import {
  AppCreateCustomerRequest,
  AppUpdateCustomerRequest,
  BookingDto,
} from '@api/generated';
import CreateBookingModal from './CreateBookingModal';
import CustomerModal from './CustomerModal';
import BlockedTimeModal from './BlockedTimeModal';
import ExportDataModal from './ExportDataModal';
import { useToast } from '@/hooks/useToast';
import { useAuthentication } from '@/features/auth/useAuthentication';

interface QuickActionsProps {
  salonId: string;
  /** Optional callback after a successful action to refresh parent data. */
  onChange?: () => void;
  /** Render the green primary button without the dashed quick-action grid. */
  primaryOnly?: boolean;
}

type ActionKey = 'booking' | 'customer' | 'blocked' | 'export';

interface StaffItem { id: string; name: string }
interface ServiceItem { id: string; name: string; price: number; duration: number }

const QuickActions: React.FC<QuickActionsProps> = ({ salonId, onChange, primaryOnly = false }) => {
  const toast = useToast();
  const { canManageBookings, canManageCustomers, canManageStaff, canManageOwnSchedule, canViewReports } = useAuthentication();
  const [open, setOpen] = useState<ActionKey | null>(null);

  const allowBooking = canManageBookings(salonId);
  const allowCustomer = canManageCustomers(salonId);
  const allowBlocked = canManageStaff(salonId) || canManageOwnSchedule(salonId);
  const allowExport = canViewReports(salonId);
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [servicesList, setServicesList] = useState<ServiceItem[]>([]);
  const [bookings, setBookings] = useState<BookingDto[]>([]);

  // Lazy-load data only when a relevant modal is going to need it.
  useEffect(() => {
    if (open === 'booking' || open === 'blocked') {
      if (staffList.length === 0) {
        staffApi
          .getActiveStaffLoV({ salonId })
          .then(r =>
            setStaffList(
              r.data.map((s: any) => ({ id: s.id, name: s.name || s.displayName }))
            )
          )
          .catch(() => undefined);
      }
    }
    if (open === 'booking' && servicesList.length === 0) {
      servicesLoVApi
        .getActiveServicesLoV({ salonId })
        .then(r =>
          setServicesList(
            r.data.map((s: any) => ({
              id: s.id,
              name: s.name,
              price: s.price ?? 0,
              duration: s.duration ?? 30,
            }))
          )
        )
        .catch(() => undefined);
    }
    if (open === 'blocked') {
      bookingsApi
        .getBookings({
          salonId,
          dateFrom: dayjs().format('YYYY-MM-DD'),
          dateTo: dayjs().add(60, 'day').format('YYYY-MM-DD'),
        })
        .then(r => setBookings(r.data))
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, salonId]);

  const handleCustomerSave = async (
    data: AppCreateCustomerRequest | AppUpdateCustomerRequest
  ) => {
    try {
      await customerApi.createCustomer({ appCreateCustomerRequest: data as AppCreateCustomerRequest });
      toast.success('Zákazník vytvořen', 'Nový zákazník byl uložen');
      onChange?.();
    } catch (e: any) {
      toast.error('Chyba', e?.message ?? 'Nepodařilo se uložit zákazníka');
      throw e;
    }
  };

  const existingBookings = bookings.map(b => ({
    id: b.id,
    start: b.start,
    end: b.end,
    customer: {
      firstName: b.customer.firstName,
      lastName: b.customer.lastName,
      email: b.customer.email,
      phone: b.customer.phone,
    },
    worker: { displayName: b.worker.displayName },
    serviceName: b.bookingLines?.[0]?.serviceName ?? '',
  }));

  return (
    <>
      {primaryOnly ? (
        allowBooking && (
          <button
            type="button"
            onClick={() => setOpen('booking')}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Nová rezervace
          </button>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {allowBooking && (
            <ActionButton
              icon={faCalendarCheck}
              label="Nová rezervace"
              hover="hover:border-green-500 hover:bg-green-50"
              onClick={() => setOpen('booking')}
            />
          )}
          {allowCustomer && (
            <ActionButton
              icon={faUsers}
              label="Nový zákazník"
              hover="hover:border-blue-500 hover:bg-blue-50"
              onClick={() => setOpen('customer')}
            />
          )}
          {allowBlocked && (
            <ActionButton
              icon={faBan}
              label="Blokovat čas"
              hover="hover:border-red-500 hover:bg-red-50"
              onClick={() => setOpen('blocked')}
            />
          )}
          {allowExport && (
            <ActionButton
              icon={faDownload}
              label="Export dat"
              hover="hover:border-purple-500 hover:bg-purple-50"
              onClick={() => setOpen('export')}
            />
          )}
        </div>
      )}

      <CreateBookingModal
        isOpen={open === 'booking'}
        onClose={() => setOpen(null)}
        onFinish={() => {
          onChange?.();
          setOpen(null);
        }}
        salonId={salonId}
        selectedDate={dayjs().format('YYYY-MM-DD')}
        staffList={staffList}
        servicesList={servicesList}
      />

      <CustomerModal
        isOpen={open === 'customer'}
        onClose={() => setOpen(null)}
        mode="create"
        onSave={handleCustomerSave}
      />

      <BlockedTimeModal
        isOpen={open === 'blocked'}
        onClose={() => setOpen(null)}
        onSuccess={() => {
          onChange?.();
          setOpen(null);
        }}
        staffList={staffList}
        existingBookings={existingBookings}
      />

      <ExportDataModal
        isOpen={open === 'export'}
        onClose={() => setOpen(null)}
        salonId={salonId}
      />
    </>
  );
};

interface ActionButtonProps {
  icon: any;
  label: string;
  hover: string;
  onClick: () => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, hover, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg transition-colors ${hover}`}
  >
    <FontAwesomeIcon icon={icon} className="w-6 h-6 text-gray-400 mr-3" />
    <span className="text-gray-600">{label}</span>
  </button>
);

export default QuickActions;
